'use client';

import { ReactNode, useEffect, useState } from 'react';

import { useBoolean } from 'usehooks-ts';
import { useAccount } from 'wagmi';

import { ConnectModal } from '@web/features/login/ConnectModal/ConnectModal';
import { useAuth } from '@web/utils/auth/manager';

import { ReconnectWallet } from './ReconnectWallet';

interface IHandlers {
  onClick: (() => void) | undefined;
  isLoading: boolean;
}

interface IProps {
  render: (handlers: IHandlers) => ReactNode;
}

type LiveAccountsState = 'unknown' | 'present' | 'absent';

// Source of truth for "is the wallet actually granting permission right now":
// `eth_accounts` is the EIP-1193 read that does NOT prompt the user — it
// returns the list of accounts the site currently has access to. Empty
// array == permission revoked, even if wagmi's `wagmi.store` still holds a
// cached `{accounts: [...]}` connection record.
async function readLiveAccounts(): Promise<readonly string[]> {
  const eth = (globalThis as { ethereum?: { request?: (a: { method: string }) => Promise<unknown> } })
    .ethereum;
  if (!eth?.request) return [];
  try {
    const result = await eth.request({ method: 'eth_accounts' });
    return Array.isArray(result) ? (result as string[]) : [];
  } catch {
    return [];
  }
}

// Shared connect-state machine for /staking surfaces.
//
//   - no JWT                       → openSiwe: open branded ConnectModal,
//                                     which mounts SignInWallet (its own
//                                     SignInRainbowKitProvider with the SIWE
//                                     auth adapter). Full SIWE flow runs on
//                                     success.
//   - JWT present, wagmi drifted   → startReconnect: mount ReconnectWallet
//                                     which disconnects wagmi then opens a
//                                     bare RainbowKitProvider modal. No SIWE
//                                     re-prompt — JWT stays untouched.
//                                     "Drifted" means wagmi's cache reports
//                                     `connected` but `eth_accounts` returns
//                                     []  — the cache outlived MetaMask's
//                                     site permission.
//   - JWT + wagmi + live accounts  → fully connected. onClick undefined,
//                                     isLoading true. Parent renders a
//                                     disabled "Loading..." button while
//                                     page-level on-chain reads settle.
export const StakingConnectButton = ({ render }: IProps) => {
  const auth = useAuth();
  const wallet = useAccount();

  const {
    value: isSiweOpen,
    setTrue: openSiwe,
    setFalse: closeSiwe,
  } = useBoolean(false);
  const {
    value: isReconnecting,
    setTrue: startReconnect,
    setFalse: stopReconnect,
  } = useBoolean(false);

  const wagmiReportsConnected =
    wallet.status === 'connected' && !!wallet.address;

  // Cross-check wagmi's cached state against MetaMask's actual permission
  // status. We re-run on every transition of `wagmiReportsConnected` or the
  // cached address so a successful reconnect flips us back to `present`.
  const [liveAccounts, setLiveAccounts] = useState<LiveAccountsState>('unknown');
  useEffect(() => {
    if (!wagmiReportsConnected) {
      setLiveAccounts('absent');
      return;
    }
    let cancelled = false;
    setLiveAccounts('unknown');
    readLiveAccounts().then((accounts) => {
      if (cancelled) return;
      setLiveAccounts(accounts.length > 0 ? 'present' : 'absent');
    });
    return () => {
      cancelled = true;
    };
  }, [wagmiReportsConnected, wallet.address]);

  const walletReallyConnected =
    wagmiReportsConnected && liveAccounts === 'present';
  // While the live check is in flight on a wagmi-says-connected page, treat
  // as "loading" so the button doesn't briefly enable + flicker between
  // states on each render.
  const isResolvingLiveAccounts =
    wagmiReportsConnected && liveAccounts === 'unknown';

  const isLoading = !!auth && (walletReallyConnected || isResolvingLiveAccounts);

  const onClick = !auth
    ? openSiwe
    : !walletReallyConnected && !isResolvingLiveAccounts
      ? startReconnect
      : undefined;

  return (
    <>
      {render({ onClick, isLoading })}
      <ConnectModal open={isSiweOpen} onClose={closeSiwe} />
      {isReconnecting && <ReconnectWallet onSettled={stopReconnect} />}
    </>
  );
};
